import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from '../landing/components/LandingNav';
import LandingFooter from '../landing/components/LandingFooter';

const modules = [
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
    ),
    title: 'Opportunities & Deal Flow',
    description: 'Centralised deal pipeline with advanced filtering, bookmarking, and comparison tools. Source infrastructure opportunities across asset classes with structured diligence workflows.',
    anchor: '#opportunities',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/>
      </svg>
    ),
    title: 'SPV Structuring',
    description: 'Automated SPV formation with jurisdiction-aware templates, multi-class cap tables, fee waterfalls, and investor syndication. Reduce legal overhead with pre-approved structures.',
    anchor: '#spv',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
      </svg>
    ),
    title: 'Pipeline & Procurement',
    description: 'Structured procurement workflows with milestone tracking, document management, and stakeholder coordination. Keep deals moving with automated task assignment and deadline alerts.',
    anchor: '#pipeline',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
      </svg>
    ),
    title: 'Portfolio Monitoring',
    description: 'Real-time portfolio dashboard with AUM metrics, performance analytics, ESG scoring, and active SPV tracking. Institutional-grade reporting at your fingertips.',
    anchor: '#portfolio',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'ESG & Reporting',
    description: 'Integrated ESG framework with configurable scoring, regulatory reporting templates, and investor-ready sustainability disclosures aligned to SFDR and TCFD standards.',
    anchor: '#esg',
  },
  {
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
      </svg>
    ),
    title: 'Data Room',
    description: 'Secure, permissioned data room with granular access controls, watermarked document distribution, version history, and full audit logging for regulatory compliance.',
    anchor: '#dataroom',
  },
];

const comparisonRows = [
  { feature: 'Deal origination & sourcing', seedDrive: true, traditional: false },
  { feature: 'Automated SPV formation', seedDrive: true, traditional: false },
  { feature: 'Integrated KYC/AML screening', seedDrive: true, traditional: false },
  { feature: 'Real-time portfolio monitoring', seedDrive: true, traditional: false },
  { feature: 'Regulatory reporting (AIFMD, SFDR)', seedDrive: true, traditional: false },
  { feature: 'RWA tokenisation pathway', seedDrive: true, traditional: false },
  { feature: 'Permissioned data room', seedDrive: true, traditional: true },
  { feature: 'Investor portal access', seedDrive: true, traditional: true },
  { feature: 'Manual document management', seedDrive: false, traditional: true },
  { feature: 'Fragmented toolchain', seedDrive: false, traditional: true },
];

export default function PlatformOverviewPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F2F0EC' }}>
      <LandingNav />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" style={{ background: '#F2F0EC' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-caption font-medium mb-6 border" style={{ borderColor: 'rgba(59,76,76,0.15)', color: 'var(--color-secondary)', background: 'rgba(59,76,76,0.04)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#3b4c4c' }}></span>
              Platform overview
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold leading-tight mb-5" style={{ color: 'var(--color-primary)' }}>One platform. Every stage of the deal.</h1>
            <p className="text-lg text-gray-600 font-body leading-relaxed">Six integrated modules covering the complete infrastructure equity lifecycle — from first look to final report — with institutional compliance built in throughout.</p>
          </div>
        </div>
      </section>
      {/* Modules */}
      <section className="">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {modules?.map((mod, i) => (
              <div
                key={mod?.title}
                className={`p-7 rounded-xl border hover:shadow-md transition-all duration-200 group ${
                  i === 0 ? 'lg:col-span-2 lg:row-span-1' : ''
                }`}
                style={{ borderColor: 'var(--color-border)' }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-5" style={{ background: 'rgba(59,76,76,0.07)', color: 'var(--color-primary)' }}>
                  {mod?.icon}
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>{mod?.title}</h3>
                <p className="text-gray-600 font-body text-sm leading-relaxed mb-4">{mod?.description}</p>
                <a
                  href={mod?.anchor}
                  className="inline-flex items-center gap-1.5 text-sm font-caption font-medium transition-colors"
                  style={{ color: 'var(--color-secondary)' }}
                >
                  Learn more
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Comparison table */}
      <section className="py-20 px-6 lg:px-8" style={{ background: '#ECEAE4' }}>
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>SeedDrive vs traditional workflow</h2>
            <p className="text-gray-600 font-body">See how SeedDrive compares to fragmented legacy approaches.</p>
          </div>
          <div className="rounded-xl border overflow-hidden shadow-sm" style={{ borderColor: 'var(--color-border)' }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: 'var(--color-primary)' }}>
                  <th className="text-left px-6 py-4 text-sm font-caption font-medium text-white">Capability</th>
                  <th className="text-center px-6 py-4 text-sm font-caption font-medium text-white">SeedDrive</th>
                  <th className="text-center px-6 py-4 text-sm font-caption font-medium text-white">Traditional</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows?.map((row, i) => (
                  <tr key={row?.feature} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-3.5 text-sm font-body text-gray-700">{row?.feature}</td>
                    <td className="px-6 py-3.5 text-center">
                      {row?.seedDrive ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M18 6L6 18M6 6l12 12"/>
                          </svg>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3.5 text-center">
                      {row?.traditional ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: 'rgba(5,150,105,0.1)', color: '#059669' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5"/>
                          </svg>
                        </span>
                      ) : (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M18 6L6 18M6 6l12 12"/>
                          </svg>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>See the platform in action</h2>
          <p className="text-gray-600 font-body mb-7">Book a personalised walkthrough with our team.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium font-caption text-white transition-all duration-200 hover:opacity-90"
            style={{ background: 'var(--color-primary)' }}
          >
            Request a Demo
          </Link>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
}
